
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, RefreshCw, ChevronLeft, ChevronRight, Send, Check, 
  AlertCircle, SlidersHorizontal, Plus, Download, FileText, ArrowRight
} from '../components/Icons';
import { Card, Table, Thead, Tbody, Tr, Th, Td, PageHeader, Button } from '../components/UI';
import { BrandLogo } from '../components/BrandLogo';
import { DocumentPreview } from '../components/DocumentPreview';
import { useTranslation } from '../contexts/LanguageContext';
import { ChannelType } from '../types';

interface EnterpriseOrder {
  id: string;
  orderNumber: string;
  date: string;
  supplier: string;
  netAmount: number;
  channel: ChannelType;
  status: 'SENT' | 'DRAFT' | 'ERROR' | 'ACKNOWLEDGED';
}

const mockEnterpriseOrders: EnterpriseOrder[] = [
  { id: '1', orderNumber: 'PO-2025-001', date: '24.10.2025', supplier: 'Supplier Portal', netAmount: 1250.50, channel: ChannelType.EDI, status: 'ACKNOWLEDGED' },
  { id: '2', orderNumber: 'PO-2025-002', date: '23.10.2025', supplier: 'Sealant Pro AG', netAmount: 420.00, channel: ChannelType.EMAIL, status: 'SENT' },
  { id: '3', orderNumber: 'PO-2025-003', date: '22.10.2025', supplier: 'ElectroHub Inc.', netAmount: 2100.00, channel: ChannelType.EBILL, status: 'DRAFT' },
  { id: '4', orderNumber: 'PO-2025-004', date: '20.10.2025', supplier: 'MetalWorks Ltd.', netAmount: 890.00, channel: ChannelType.PRINT, status: 'SENT' },
  { id: '5', orderNumber: 'PO-2025-005', date: '19.10.2025', supplier: 'ChemCo Systems', netAmount: 150.75, channel: ChannelType.EMAIL, status: 'ERROR' },
];

const EnterpriseOrders: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('All');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [globalSearch, setGlobalSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [viewingOrder, setViewingOrder] = useState<EnterpriseOrder | null>(null);

  const tabs = ['All', 'Sent', 'Draft', 'Errors'];

  const handleColumnFilterChange = (key: string, value: string) => {
    setColumnFilters(prev => ({ ...prev, [key]: value }));
  };

  const filteredData = useMemo(() => {
    return mockEnterpriseOrders.filter(order => {
      // Tab Filter
      if (activeTab === 'Sent' && order.status !== 'SENT' && order.status !== 'ACKNOWLEDGED') return false;
      if (activeTab === 'Draft' && order.status !== 'DRAFT') return false;
      if (activeTab === 'Errors' && order.status !== 'ERROR') return false;

      // Global Search
      if (globalSearch) {
        const s = globalSearch.toLowerCase();
        const matchesGlobal = order.orderNumber.toLowerCase().includes(s) || order.supplier.toLowerCase().includes(s);
        if (!matchesGlobal) return false;
      }

      // Column Filters
      return Object.entries(columnFilters).every(([key, value]) => {
        if (!value) return true;
        const itemValue = String((order as any)[key] || '').toLowerCase();
        return itemValue.includes((value as string).toLowerCase());
      });
    });
  }, [activeTab, globalSearch, columnFilters]);

  // Removed toggleSelectRow as it is no longer used by the checkbox column

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACKNOWLEDGED':
        return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest bg-green-50 text-green-700 border border-green-200"><Check size={10} strokeWidth={4}/> Acknowledged</span>;
      case 'SENT':
        return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest bg-blue-50 text-blue-700 border border-blue-200"><Send size={10} strokeWidth={4}/> Sent</span>;
      case 'DRAFT':
        return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest bg-gray-50 text-gray-500 border border-gray-200">Draft</span>;
      case 'ERROR':
        return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest bg-red-50 text-red-700 border border-red-200"><AlertCircle size={10} strokeWidth={4}/> Failed</span>;
      default: return null;
    }
  };

  if (viewingOrder) {
    // Transform to fit DocumentPreview interface
    const doc = {
      orderNumber: viewingOrder.orderNumber,
      date: viewingOrder.date,
      customer: 'VMI Services AG', // Since this is an outbound PO from the enterprise
      vendor: viewingOrder.supplier,
      net: viewingOrder.netAmount,
      gross: viewingOrder.netAmount * 1.081,
      status: viewingOrder.status
    };
    return <DocumentPreview type="ORDER" document={doc} onBack={() => setViewingOrder(null)} />;
  }

  return (
    <div className="p-6 space-y-6 max-w-[1920px] mx-auto animate-in fade-in duration-500">
      <PageHeader 
        title="Purchase Requests" 
        breadcrumbs={[{ label: 'Supply Chain' }, { label: 'Order Requests' }]}
        actions={
          <Button className="shadow-lg flex items-center gap-2">
            <Plus size={16} strokeWidth={3} /> New Order Request
          </Button>
        }
      />

      <Card className="overflow-hidden border-t-4 border-t-brand-medium">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-[#393939] bg-white dark:bg-[#161616] flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-1 bg-[#F5F7FA] dark:bg-[#262626] p-1 rounded-md">
            {tabs.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`h-8 px-4 text-[10px] font-bold uppercase tracking-widest transition-all rounded-sm ${
                  activeTab === tab 
                    ? 'bg-white dark:bg-[#161616] text-brand-dark dark:text-white shadow-sm' 
                    : 'text-gray-500 hover:text-brand-dark dark:hover:text-white'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
             <div className="relative group">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-medium transition-colors" />
                <input 
                  type="text" 
                  placeholder={t('search_requests')} 
                  value={globalSearch}
                  onChange={(e) => setGlobalSearch(e.target.value)}
                  className="h-9 pl-9 pr-4 bg-[#F5F7FA] dark:bg-[#262626] border border-transparent focus:border-brand-medium focus:bg-white dark:focus:bg-[#1F1F1F] rounded-sm text-xs w-64 outline-none transition-all"
                />
             </div>
             <button className="h-9 px-4 flex items-center gap-2 bg-white dark:bg-[#262626] border border-gray-200 dark:border-[#393939] hover:bg-gray-50 text-gray-600 dark:text-gray-300 text-[10px] font-bold uppercase tracking-widest rounded-sm transition-all">
                <Download size={14} /> Export
             </button>
          </div>
        </div>

        <Table>
          <Thead>
            <Tr isHeader>
              <Th>
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">Request Number</span>
                  <input 
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                    placeholder={t('filter_placeholder')}
                    onChange={(e) => handleColumnFilterChange('orderNumber', e.target.value)}
                  />
                </div>
              </Th>
              <Th>
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">Supplier</span>
                  <input 
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                    placeholder={t('filter_placeholder')}
                    onChange={(e) => handleColumnFilterChange('supplier', e.target.value)}
                  />
                </div>
              </Th>
              <Th className="text-right">
                <div className="flex flex-col gap-2 items-end">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">Amount (CHF)</span>
                  <input 
                    className="w-24 bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400 text-right"
                    placeholder={t('filter_placeholder')}
                    onChange={(e) => handleColumnFilterChange('netAmount', e.target.value)}
                  />
                </div>
              </Th>
              <Th>
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">Status</span>
                  <select 
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-1 focus:outline-none focus:border-brand-medium transition-colors cursor-pointer text-gray-600 dark:text-gray-300"
                    onChange={(e) => handleColumnFilterChange('status', e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="ACKNOWLEDGED">Acknowledged</option>
                    <option value="SENT">Sent</option>
                    <option value="DRAFT">Draft</option>
                    <option value="ERROR">Failed</option>
                  </select>
                </div>
              </Th>
              <Th className="text-right">
                 Actions
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredData.map((order) => (
              <Tr key={order.id} onClick={() => setViewingOrder(order)}>
                <Td>
                   <div className="flex flex-col">
                      <span className="font-bold text-brand-dark dark:text-white font-mono">{order.orderNumber}</span>
                      <span className="text-[10px] text-gray-400">{order.date}</span>
                   </div>
                </Td>
                <Td className="text-sm font-medium text-gray-700 dark:text-gray-300">{order.supplier}</Td>
                <Td className="text-right font-black text-brand-dark dark:text-white">
                   {order.netAmount.toLocaleString('de-CH', { minimumFractionDigits: 2 })}
                </Td>
                <Td>{getStatusBadge(order.status)}</Td>
                <Td className="text-right">
                   <div className="flex items-center justify-end gap-2">
                      {order.status === 'DRAFT' && (
                         <Button size="sm" className="h-7 px-3 text-[9px]" onClick={(e) => e.stopPropagation()}>
                            <Send size={10} className="mr-1.5"/> SEND NOW
                         </Button>
                      )}
                      <button className="p-2 text-gray-300 hover:text-brand-dark dark:hover:text-white" onClick={() => setViewingOrder(order)}>
                         <ArrowRight size={16} />
                      </button>
                   </div>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Card>
    </div>
  );
};

export default EnterpriseOrders;
